import { Button } from "@hortis/ui/button";
import { Dialog } from "@hortis/ui/dialog";
import { Move, Plus, SwitchHorizontal1 } from "@hortis/ui/icons";
import { toast } from "@hortis/ui/sonner";
import { useCallback, useState } from "react";
import { useWatch } from "react-hook-form";
import { MapPreviewOverlay } from "src/components/map/overlays/map-preview-overlay";
import { SetInitialViewMapOverlay } from "src/components/map/overlays/set-initial-view-map-overlay";
import { MapDialogContent } from "src/components/map/position-field/map-position-modal";
import { useMaplibre } from "src/components/map/utils";
import { FormTextField } from "src/components/text-field";
import { ControlledMap } from "src/features/records/material/components/material-title-card/controlled-map";
import type { SiteCenterPointIn } from "./schema";

interface MaterialMapPositionFieldProps {
  value: SiteCenterPointIn | null;
  onChange: (value: SiteCenterPointIn | null) => void;
  trigger: () => Promise<boolean>;
  name: string;
}

export const InitialMapViewPicker = ({
  value: _,
  onChange,
  trigger,
  name,
}: MaterialMapPositionFieldProps) => {
  const [showFields, setShowFields] = useState(false);
  const value = useWatch<Record<typeof name, SiteCenterPointIn | null>>({
    name,
  });

  const toggleFields = useCallback(async () => {
    if (
      value?.latitude == null &&
      value?.longitude == null &&
      value?.zoom == null
    ) {
      setShowFields((x) => !x);
      return;
    }

    const valid = await trigger();
    if (valid) {
      setShowFields((x) => !x);
    }
  }, [trigger, value]);

  const [dialogOpen, setDialogOpen] = useState(false);

  const { "dialog-map": dialogMap } = useMaplibre();

  const numericValue =
    value == null
      ? null
      : {
          latitude: Number(value.latitude),
          longitude: Number(value.longitude),
          zoom: Number(value.zoom),
        };
  const positioned = value != null && numericValue != null;

  return (
    <>
      {showFields ? (
        <div className="flex flex-col gap-8 rounded-lg border border-grey-200 bg-grey-50 px-4 py-6 shadow-xs md:px-8 md:py-8">
          <FormTextField
            label="Latitude"
            name="centerPoint.latitude"
            testId="latitude"
            type="text"
            signedNumeric
            decimal
          />
          <FormTextField
            label="Longitude"
            name="centerPoint.longitude"
            testId="longitude"
            type="text"
            signedNumeric
            decimal
          />
          <FormTextField
            label="Zoom level"
            name="centerPoint.zoom"
            testId="zoom"
            type="text"
            helperText="Most sites fit between 16-19 zoom level"
            numeric
            decimal
          />
        </div>
      ) : (
        <div className="relative h-52">
          <ControlledMap
            useSiteCoordinates={value == null}
            zoom={numericValue?.zoom ?? 2}
            cursor="default"
            latitude={numericValue?.latitude ?? 0}
            longitude={numericValue?.longitude ?? 0}
            staticMap
          />
          <MapPreviewOverlay blur={!positioned}>
            <Button
              size="xs"
              startIcon={positioned ? Move : Plus}
              testId={
                positioned ? "edit-position-button" : "add-position-button"
              }
              onClick={() => {
                setDialogOpen(true);
              }}
            >
              {positioned ? "Update" : "Add initial view"}
            </Button>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <MapDialogContent
                initialPosition={numericValue}
                zoom={numericValue?.zoom ?? 2}
              >
                <SetInitialViewMapOverlay
                  className="rounded-2xl"
                  onClose={() => {
                    setDialogOpen(false);
                  }}
                  confirmInitialView={() => {
                    if (dialogMap == null) {
                      toast.error("Failed to get map instance");
                      return;
                    }
                    const { lat, lng } = dialogMap.getCenter();
                    const zoom = dialogMap.getZoom();
                    onChange({
                      latitude: String(lat),
                      longitude: String(lng),
                      zoom: String(zoom),
                    });
                    setDialogOpen(false);
                  }}
                  baseMap={null}
                />
              </MapDialogContent>
            </Dialog>
          </MapPreviewOverlay>
        </div>
      )}
      <div className="flex justify-between gap-2 px-2 pt-2">
        <Button
          variant="linkgray"
          onClick={toggleFields}
          testId="toggle-map-fields-button"
          startIcon={<SwitchHorizontal1 className="h-4 w-4 text-grey-500" />}
        >
          Use {showFields ? "map" : "coordinates"}
        </Button>
      </div>
    </>
  );
};
